import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import QRCode from "qrcode";
import { config } from "../../utils/Constants";
import useErrorHandler from "../../components/ErrorHandler";
import {
  setFeatureData,
  settingData,
  setWebSocketToken,
} from "../store/slices/appSlice";
import { setWebSocket } from "../../features/webSocketSlice";
import useNewGenerateToken from "../authentication/GenerateToken";
import { SESSION_STORAGE_KEYS } from "../constants/sessionStorage";
import {
  buildSidebarPages,
  notifySidebarSessionUpdated,
} from "../Components/Sidebar/utils/sidebarStorage";

const useNewLoginAPI = () => {
  const { ErrorHandler } = useErrorHandler();
  const { generateToken } = useNewGenerateToken();
  const [isLoading, setIsLoading] = useState(true);
  const [mobileLoginData, setMobileLoginData] = useState([]);
  const [deepLinkData, setDeepLinkData] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const PortalOnBoard = async (setQrTimeOut, setQrUrl, code) => {
    try {
      setQrTimeOut(false);
      setIsLoading(true);

      const res = await axios.post(`${config.url}/v1/Auth/PortalOnBoard`, {
        Code: code,
      });

      sessionStorage.setItem(
        SESSION_STORAGE_KEYS.SESSION_ID,
        res.data.Payload.SessionID,
      );
      setDeepLinkData(res.data.Payload.deeplink || null);
      setQrUrl(await QRCode.toDataURL(res.data.Payload.QR));
      setIsLoading(false);
    } catch (error) {
      ErrorHandler(error);
      setIsLoading(false);
      setQrTimeOut(true);
    }
  };

  const checkQrAxios = async () => {
    const res = await axios.post(
      `${config.url}/v1/Auth/CheckQrStatus`,
      {
        SessionID: sessionStorage.getItem(SESSION_STORAGE_KEYS.SESSION_ID),
        Code: sessionStorage.getItem(SESSION_STORAGE_KEYS.REG_CODE_URL) || "",
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const result = res.data.Payload;
    if (!result) return res;

    sessionStorage.setItem(
      SESSION_STORAGE_KEYS.FEATURES,
      JSON.stringify(result.Categories),
    );
    sessionStorage.setItem(
      SESSION_STORAGE_KEYS.TABS,
      JSON.stringify(result.Tabs),
    );
    sessionStorage.setItem(
      SESSION_STORAGE_KEYS.SIDEBAR_PAGES,
      JSON.stringify(buildSidebarPages(result.Categories, result.Tabs)),
    );
    dispatch(
      setFeatureData({
        section: "merchant",
        key: "sidebar",
        value: buildSidebarPages(result.Categories, result.Tabs),
      }),
    );
    dispatch(
      setFeatureData({
        section: "merchant",
        key: "permissions",
        value: Array.isArray(result.Categories) ? result.Categories : [],
      }),
    );
    dispatch(
      setFeatureData({
        section: "merchant",
        key: "features",
        value: Array.isArray(result.Categories) ? result.Categories : [],
      }),
    );
    dispatch(
      setFeatureData({
        section: "merchant",
        key: "tabs",
        value: Array.isArray(result.Tabs) ? result.Tabs : [],
      }),
    );
    
    dispatch(
      settingData({
        field: "settingObjectData",
        value: {
          checkqrstatus: result,
          entityType: result.EntityType,
          entityId: result.EntityID,
          legalEntityId: result.LegalEntityID,
          categories: Array.isArray(result.Categories) ? result.Categories : [],
          tabs: Array.isArray(result.Tabs) ? result.Tabs : [],
        },
      }),
    );
    sessionStorage.setItem(SESSION_STORAGE_KEYS.ENTITY_TYPE, result.EntityType);
    sessionStorage.setItem("EntityRole", result.Role);
    sessionStorage.setItem(SESSION_STORAGE_KEYS.ENTITY_ID, result.EntityID);
    sessionStorage.setItem(
      SESSION_STORAGE_KEYS.LEGAL_ENTITY_ID,
      result.LegalEntityID,
    );
    sessionStorage.setItem(SESSION_STORAGE_KEYS.EMPLOYEE_ID, result.EmployeeID);
    sessionStorage.setItem(SESSION_STORAGE_KEYS.WEB_KEY, result.WebKey);
    sessionStorage.setItem("EmployeeEmail", result?.EmployeeEmail);
    sessionStorage.setItem("IsAdvanceAllowed", result?.IsAdvanceAllowed);
    sessionStorage.setItem("AccountType", JSON.stringify(result?.Accounts));
    sessionStorage.setItem("BslStatus", result?.BslStatus);

    if (result.SwitchLegalEntities?.length) {
      sessionStorage.setItem(
        SESSION_STORAGE_KEYS.ENTITIES,
        JSON.stringify(result.SwitchLegalEntities),
      );

      if (result.EntityType === "LEGALENTITY") {
        const legalEntity = result.SwitchLegalEntities.find(
          (entity) => entity.LegalEntityID === result.EntityID,
        );

        sessionStorage.setItem("EntityName", legalEntity?.LegalEntityName || "");
        sessionStorage.setItem("EntityURL", legalEntity?.LegalEntityIconUrl || "");
        sessionStorage.setItem("UserName", legalEntity?.UserName || "");
        sessionStorage.setItem("EntityAddress", legalEntity?.EntityAddress || "");
        sessionStorage.setItem("EntityMobile", legalEntity?.MobileNo || "");
        sessionStorage.setItem("UserIconUrl", legalEntity?.UserIconUrl || "");
        sessionStorage.setItem(
          "LegalEntityName",
          legalEntity?.LegalEntityName || "",
        );
      }
    }

    if (result.SwitchMerchants?.length) {
      sessionStorage.setItem(
        SESSION_STORAGE_KEYS.MERCHANTS,
        JSON.stringify(result.SwitchMerchants),
      );

      if (result.EntityType === "MERCHANTEMPLOYEE") {
        const merchant = result.SwitchMerchants.find(
          (entity) => entity.MerchantID === result.EntityID,
        );

        sessionStorage.setItem("EntityName", merchant?.MerchantName || "");
        sessionStorage.setItem("EntityURL", merchant?.MerchantIconUrl || "");
        sessionStorage.setItem("UserName", merchant?.UserName || "");
        sessionStorage.setItem("EntityURLMer", merchant?.MerchantIconUrl || "");
      }
    } else {
      sessionStorage.setItem(SESSION_STORAGE_KEYS.MERCHANTS, null);
    }

    notifySidebarSessionUpdated();

    return res;
  };

  const CheckQrStatus = async (timerStatus, setQrTimeOut) => {
    try {
      const res = await checkQrAxios();
      const status = res.data.Payload.SessionStatus;

      dispatch(settingData({ field: "checkqrstatus", value: res.data.Payload }));

      if (status.toUpperCase() === "ACTIVE") {
        clearInterval(timerStatus);
        setIsLoading(true);
        await generateToken(
          sessionStorage.getItem(SESSION_STORAGE_KEYS.SESSION_ID),
          timerStatus,
        );
      }

      if (status.toUpperCase() === "SELF_REGISTERING") {
        clearInterval(timerStatus);
        setIsLoading(false);
        setQrTimeOut(true);
        navigate("/enrollment/1");
      }

      return {
        status: res.status,
        sessionStatus: status,
      };
    } catch (error) {
      setIsLoading(false);
      clearInterval(timerStatus);
      throw error;
    }
  };

  const MobileLogin = async (
    userAccountId,
    timeStamp,
    reference,
    action,
    secureHash,
  ) => {
    try {
      const res = await axios.post(`${config.url}/V1/AuthMobile/MobileLogIn`, {
        UserAccountId: userAccountId,
        TimeStamp: timeStamp,
        Reference: reference,
        ActionTypeId: action,
        SecureHash: secureHash,
      });

      const result = res.data.Payload;
      if (result) {
        sessionStorage.setItem(SESSION_STORAGE_KEYS.MOBILE_TOKEN, result.Token);
        sessionStorage.setItem(
          SESSION_STORAGE_KEYS.MOBILE_SESSION,
          result.SessionID,
        );
        sessionStorage.setItem(
          "IsMobileLogIn",
          String(Boolean(result?.CheckQRStatusResponse?.IsMobileLogIn)),
        );
        const webSocketToken =
          result.WebSocketToken || result.WebSokcetToken || "";
        dispatch(setWebSocket(webSocketToken));
        dispatch(setWebSocketToken(webSocketToken));
      }

      setMobileLoginData(res.data);
    } catch (error) {
      ErrorHandler(error);
      throw error;
    }
  };

  return {
    isLoading,
    PortalOnBoard,
    CheckQrStatus,
    checkQrAxios,
    MobileLogin,
    mobileLoginData,
    deepLinkData,
  };
};

export default useNewLoginAPI;
